/**
 * TldTable.tsx — prisliste per domeneendelse (ibm.com data-table-stil)
 *
 * Semantisk <table> med <caption>, scope=col/row. Ingen ytre ramme, grå
 * overskriftsrad, 14px-rader med hårlinje, og tabellen bløder 16px utenfor
 * innholdskanten som IBMs tabeller. Wrapperen er scrollbar og tastatur-
 * fokuserbar på smale skjermer. Mest populære endelser får en ren eyebrow-tekst under endelsen.
 */

import { formatPrice } from '@/lib/domain';
import type { DomainPriceTableContent, DomainTld } from '@/types/domain';
import SectionHeader from '@/components/product/SectionHeader';
import shared from '@/components/product/product.module.css';
import styles from './domain.module.css';

interface TldTableProps {
  id: string;
  content: DomainPriceTableContent & { popular: string };
  items: DomainTld[];
  currency: string;
}

export default function TldTable({ id, content, items, currency }: TldTableProps) {
  const c = content.columns;
  // Er alle radene populære, sier merket ingenting; da vises det ikke
  const showBand = items.some((row) => !row.popular);

  return (
    <section id={id} className={shared.sectionAlt}>
      <div className={shared.container}>
        <SectionHeader id={`${id}-heading`} eyebrow={content.eyebrow} heading={content.heading}>
          <p className={shared.lead}>{content.intro}</p>
        </SectionHeader>

        <div className={styles.tableWrap} tabIndex={0} role="region" aria-labelledby={`${id}-heading`}>
          <table className={styles.table}>
            <caption className={shared.srOnly}>{content.heading}</caption>
            <thead>
              <tr>
                <th scope="col">{c.tld}</th>
                <th scope="col">{c.register}</th>
                <th scope="col">{c.renew}</th>
                <th scope="col">{c.transfer}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.tld}>
                  <th scope="row">
                    <span className={styles.tldName}>.{row.tld}</span>
                    {showBand && row.popular && <span className={styles.tldPopular}>{content.popular}</span>}
                  </th>
                  <td>{formatPrice(row.register, currency)}</td>
                  <td>{formatPrice(row.renew, currency)}</td>
                  <td>{formatPrice(row.transfer, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.footnote}>{content.footnote}</p>
      </div>
    </section>
  );
}
